<template>
  <div>
    <PriorHeader />
    <v-alert
      v-if="errorMsg"
      class="error-msg"
      :text="errorMsg"
      type="error"
      variant="outlined"
      :closable="true"></v-alert>
    <div class="d-flex flex-column align-center mt-8">
      <v-container :fluid="true" class="lh--container-wide review-container">
        <v-row>
          <v-col cols="12">
            <h1 class="text-h4">Document Review</h1>
          </v-col>
          <v-col cols="12">
            <v-row no-gutters>
              <v-col v-if="!showLoader" cols="8">
                <PatientBar :patient="patient" :client-company="order?.clientCompany.id" />
              </v-col>
              <v-col v-if="!showLoader" cols="4">
                <ClassificationBar />
              </v-col>
              <v-container v-if="showLoader">
                <v-skeleton-loader type="text@4" />
              </v-container>
            </v-row>
          </v-col>
          <v-col cols="12" class="bg-snow rounded-lg">
            <v-skeleton-loader v-if="showLoader || !currentPage || !order" type="image@8" />
            <PagePreview
              v-if="!showLoader && currentPage && order && requirementsStore.clientConfig"
              ref="previewRef"
              :editable="false"
              :order-form="order.orderForm"
              :insurance-cards="order.insuranceCard ? [order.insuranceCard] : []"
              :stage="intakeStatus"
              :tags="order?.pageTags[currentPage.id]"
              @data-form-selected="onDataFormSelected"
              @attribute-edit="onAttributeEdit"
              @snooze="onSnooze"
              @archive="onArchive">
              <template #before>
                <v-container v-if="checklistGroups">
                  <v-row>
                    <v-col class="text-navy-dark weight-600 text-center">
                      Attach pages needed for prior authorization submission.
                      <br />
                      Verify payor requirements are met with the checklist.
                      <br />
                      [ Checklist generated for medication: {{ checklistMedicationName }} ]
                    </v-col>
                  </v-row>
                  <v-row v-for="[groupName, group] in Object.entries(checklistGroups)" :key="groupName" no-gutters>
                    <v-col class="my-2">
                      <v-card color="surface" class="pa-3">
                        <v-row>
                          <v-col class="weight-600 text-navy-dark">
                            <v-checkbox :model-value="checklistChecks[groupName]" :label="groupName" :readonly="true" />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-expansion-panels color="surface">
                            <v-expansion-panel v-for="[subCategory, items] in Object.entries(group)" :key="subCategory">
                              {{ void (hasCurrentPage = items[0]?.pageIds?.indexOf(currentPage?.id) > -1) }}
                              <v-expansion-panel-title
                                collapse-icon=""
                                expand-icon=""
                                class="text-body-1 text-navy-dark">
                                <v-row class="ma-0">
                                  <v-col cols="12">
                                    {{ subCategory }}
                                  </v-col>
                                  <v-col cols="8">
                                    <span class="text-body-2 text-blue-gray weight-600">Found on:</span>
                                    <v-chip
                                      v-for="pageId in items[0].pageIds
                                        .filter((pageId) => pageManager.getPageNumber(pageId) !== -1)
                                        .sort((a, b) => pageManager.getPageNumber(a) - pageManager.getPageNumber(b))"
                                      :key="pageId"
                                      density="compact"
                                      rounded="sm"
                                      closable
                                      size="small"
                                      color="blue-gray"
                                      class="ml-1"
                                      @click:close="() => onRemovePageCategory(items[0], pageId)">
                                      <template #close>
                                        <v-icon icon="mdi-close" size="15"></v-icon>
                                      </template>
                                      Page {{ pageManager.getPageNumber(pageId) + 1 }}
                                    </v-chip>
                                  </v-col>
                                  <v-col align="right">
                                    <v-btn-lh
                                      v-if="!hasCurrentPage"
                                      size="small"
                                      color="primary"
                                      class="text-body-1 mb-2"
                                      @click.stop="onAddPageCategory(groupName, subCategory)">
                                      Add Page
                                    </v-btn-lh>
                                    <v-btn-lh
                                      v-if="hasCurrentPage"
                                      size="small"
                                      color="secondary"
                                      class="text-body-1 mb-2"
                                      @click.stop="onRemovePageCategory(items[0], currentPage?.id)">
                                      Remove Page
                                    </v-btn-lh>
                                  </v-col>
                                </v-row>
                              </v-expansion-panel-title>
                              <v-expansion-panel-text>
                                <pre v-for="item in items" :key="item.id" class="unstyled">{{ item.description }}</pre>
                              </v-expansion-panel-text>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-card color="surface" class="pa-3 weight-600 primary-text">
                        <v-checkbox
                          v-model="attachPolicySummary"
                          label="Select to attach summary of proof of meeting policy requirements" />
                        <v-btn-lh class="w-100" @click="onPreviewPolicySummary">Preview Policy Summary</v-btn-lh>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" class="text-accent">
                      <v-progress-linear color="accent" :model-value="checklistCompletion"></v-progress-linear>
                      <br />
                      {{ checklistCompletion.toFixed(0) }}% complete
                    </v-col>
                  </v-row>
                </v-container>
              </template>
            </PagePreview>
            <v-sheet rounded="xl" class="pa-4 mt-4 mb-n4 ms-n4 me-n4" elevation="1">
              <BottomBar :intake-status="intakeStatus" />
              <v-form class="clinical-footer" @submit.prevent>
                <v-container>
                  <v-row>
                    <v-col cols="auto">
                      <v-btn-lh class="text-body-1" :loading="showLoader" @click="onBack">Go Back</v-btn-lh>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn-lh class="text-body-1" :loading="showLoader" @click="onChangeInsurance">
                        Change Insurance
                      </v-btn-lh>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn-lh color="secondary" :loading="showLoader" @click="onReferralThankYou">
                        Referral Thank You
                      </v-btn-lh>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn-lh color="secondary" :loading="showLoader" @click="onRequestMissingInformation">
                        Request Missing Information
                      </v-btn-lh>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn-lh :loading="showLoader" :disabled="buttonDisabled" @click="submitStep">
                        No Missing Information, Start Prior Authorization
                      </v-btn-lh>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
              <v-container v-if="missingInformationVisible" max-width="900">
                <v-row>
                  <v-col>Message to Provider</v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <OutgoingFaxEditor
                      v-if="order"
                      title=""
                      :types="[TEMPLATE_TYPES.INTAKE_MISSING_INFORMATION]"
                      :intake-order="order"
                      :to-fax-number="order.orderForm?.providerFax"
                      @on-complete="onRequestMissingInformation" />
                  </v-col>
                </v-row>
              </v-container>
              <v-container v-if="referralThankYouVisible" max-width="900">
                <v-row>
                  <v-col>Referral Thank You Message</v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <OutgoingFaxEditor
                      v-if="order"
                      title=""
                      :types="[TEMPLATE_TYPES.INTAKE_REFERAL_THANK_YOU]"
                      :intake-order="order"
                      :to-fax-number="order.orderForm?.providerFax"
                      @on-complete="onReferralThankYou" />
                  </v-col>
                </v-row>
              </v-container>
            </v-sheet>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <br />
            <AuditLogList :intake-order-id="orderId" />
          </v-col>
        </v-row>
      </v-container>
    </div>
    <PriorFooter />
    <v-dialog v-model="showPolicySummary" max-width="900">
      <v-card>
        <v-card-title>Preview Policy Proof Page</v-card-title>
        <v-card-text>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <v-sheet color="surface" class="pa-4" v-html="policySummary"></v-sheet>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showArchiveReasonDialog">
      <ArchiveNote :intake-order="order" @on-archived="onArchived" />
    </v-dialog>
  </div>
</template>

<script setup>
import PriorHeader from "@/components/PriorHeader.vue";
import PriorFooter from "@/components/PriorFooter.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import { useDate } from "vuetify";
import { onMounted, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { intakeService } from "@/services/intakeService";
import ArchiveNote from "@/components/ArchiveNote.vue";
import PagePreview from "@/components/intakeReview/PagePreview.vue";
import OutgoingFaxEditor from "@/components/OutgoingFaxEditor.vue";
import PatientBar from "@/components/intakeReview/PatientBar.vue";
import BottomBar from "@/components/intakeReview/BottomBar.vue";
import ClassificationBar from "@/components/intakeReview/ClassificationBar.vue";
import { DOCUMENT_CLASSIFICATIONS, INTAKE_STAGE, IntakeDocumentPage, IntakeOrder } from "@/models/intake";
import { useIntakeStore } from "@/stores/intakeStore";
import { TEMPLATE_TYPES } from "@/models/templates";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { handleFormValidationErrors } from "@/utils/validators";

const intakeStore = useIntakeStore();
const requirementsStore = useRequirementsStore();
const dateAdapter = useDate();
const showLoader = ref(true);
const intakeStatus = ref("Clinical Review");
const pageManager = intakeStore.pageManager;
const currentPage = computed(() => intakeStore.pageManager.currentPage);
const route = useRoute();
const orderId = Number.parseInt(route.params.id);
const order = computed(() => intakeStore.intakeOrder);
const patient = computed(() => order.value?.patient ?? null);
const router = useRouter();
const errorMsg = ref(undefined);
const missingInformationVisible = ref(false);
const referralThankYouVisible = ref(false);
const attachPolicySummary = ref(false);
const showPolicySummary = ref(false);
const orderUpdating = ref(false);
const buttonDisabled = ref(false);
const showArchiveReasonDialog = ref(false);
const checklistMedicationName = computed(() => {
  // We assume the first checklist item is the medication name
  return order.value?.policyChecklist?.[0]?.medicationName.toUpperCase() || "DEFAULT";
});

const checklistGroups = computed(() => order.value?.checklistGroups);
const checklistCompletion = computed(() => {
  // We only add pages to the first checklist item
  const pageCounts = {};
  order.value.policyChecklist.forEach((item) => {
    if (item.category) {
      pageCounts[item.category] = pageCounts[item.category] ?? 0;
      pageCounts[item.category] += item.pageIds.length;
    }
  });
  const totalCount = Object.keys(pageCounts).length;
  const completedCount = Object.values(pageCounts).filter((count) => count > 0).length;
  return Math.round((completedCount / totalCount) * 100);
});
const checklistChecks = computed(() => {
  const checks = {};
  Object.entries(order.value?.checklistGroups).map(([groupName, group]) => {
    checks[groupName] = false;
    Object.values(group).forEach((items) => {
      if (items[0].pageIds.length > 0) {
        checks[groupName] = true;
      }
    });
  });
  return checks;
});

/**
 * Generate a summary of the policy proof page
 * This is used to preview the policy proof page before it is generated
 * The summary is grouped by checklist group and sub-category
 */
const policySummary = computed(() => {
  let summary = "";
  if (!order.value) {
    return summary;
  }

  // Get all pages in order they will appear in the final document
  const allPages = pageManager.pages;

  // Generate the summary grouped by checklist group and sub-category
  Object.entries(order.value.checklistGroups || {}).forEach(([groupName, group]) => {
    summary += `<b style="font-size: 1.1em;">${groupName}</b><br />`;
    Object.entries(group || {}).forEach(([subCategory, items]) => {
      summary += `<b>${subCategory}</b> `;
      items.forEach((item) => {
        const pageNumbers = (item.pageIds || [])
          .filter((pageId) => {
            const pageIndex = allPages.findIndex((p) => p.id === pageId);
            return pageIndex !== -1; // Only include pages that exist
          })
          .sort((a, b) => {
            const pageIndexA = allPages.findIndex((p) => p.id === a);
            const pageIndexB = allPages.findIndex((p) => p.id === b);
            return pageIndexA - pageIndexB;
          })
          .map((pageId) => {
            // Find the index of the page in the full list of pages
            const pageIndex = allPages.findIndex((p) => p.id === pageId);
            return `Page ${pageIndex + 1}`;
          })
          .join(", ");
        if (pageNumbers) {
          summary += `(${pageNumbers})`;
        }
        summary += "<br />";
        summary += item.description.replace(/\n/g, "<br />");
      });
      summary += "<br />";
    });
    summary += "<br />";
  });
  return summary;
});

/**
 * Update the policy summary when the policy summary is generated
 */
watch(policySummary, (newSummary) => {
  if (order.value && attachPolicySummary.value) {
    order.value.updateAttribute("policySummary", btoa(encodeURIComponent(newSummary)));
    order.value.debouncedSaveUpdates();
  }
});

/**
 * Update the policy summary when the checkbox is checked or unchecked
 */
watch(attachPolicySummary, (newAttachPolicySummary) => {
  if (order.value) {
    if (newAttachPolicySummary) {
      order.value.updateAttribute("policySummary", btoa(encodeURIComponent(policySummary.value)));
    } else {
      order.value.updateAttribute("policySummary", "");
    }
    order.value.debouncedSaveUpdates();
  }
});

const previewRef = ref(null);

onMounted(async () => {
  intakeStore.reset();
  intakeStore.stage = INTAKE_STAGE.CLINICAL_REVIEW;
  await fetchIntakeOrder();
  await requirementsStore.refreshClientConfig(intakeStore.intakeOrder?.clientCompany?.id);
  await fetchPages();
  if (order.value?.policySummary) {
    attachPolicySummary.value = true;
  }
});

window.addEventListener("beforeunload", () => {
  // Fire and forget the save updates
  order.value?.saveUpdates();
});

const onPreviewPolicySummary = () => {
  showPolicySummary.value = true;
};

const onAddPageCategory = async (groupName, subCategory) => {
  errorMsg.value = "";
  try {
    orderUpdating.value = true;
    const checklist = order.value.policyChecklist.find(
      (c) => c.category === groupName && c.subCategory === subCategory
    );
    if (checklist && checklist.pageIds.indexOf(currentPage.value.id) === -1) {
      checklist.pageIds.push(currentPage.value.id);
      order.value.updateAttribute("policyChecklist", order.value.policyChecklist);
      await order.value.debouncedSaveUpdates();
    }
  } catch (ex) {
    errorMsg.value = "Error adding the page category";
  } finally {
    orderUpdating.value = false;
  }
};

const onRemovePageCategory = async (checklistItem, pageId) => {
  // We're assuming the checklistItem is a reference from `order.policyChecklist`, so we don't need to find the object again
  checklistItem.pageIds = checklistItem.pageIds.filter((id) => id !== pageId);
  order.value.updateAttribute("policyChecklist", order.value.policyChecklist);
  await order.value.debouncedSaveUpdates();
};

const onChangeInsurance = async () => {
  try {
    showLoader.value = true;
    await intakeService.postUpdateOrderStatus(order.value.id, INTAKE_STAGE.QUALIFY_PATIENT);
    router.push({ name: "qualify-order", params: { id: order.value.id } });
  } catch (ex) {
    errorMsg.value = ex.message;
  } finally {
    showLoader.value = false;
  }
};

const onBack = () => {
  router.push({ name: "all-patients" });
};

const onRequestMissingInformation = () => {
  missingInformationVisible.value = !missingInformationVisible.value;
  if (missingInformationVisible.value) {
    referralThankYouVisible.value = false;
  }
};

const onReferralThankYou = () => {
  referralThankYouVisible.value = !referralThankYouVisible.value;
  if (referralThankYouVisible.value) {
    missingInformationVisible.value = false;
  }
};

const onDataFormSelected = (dataForm) => {
  pageManager.goToDataFormPage(dataForm);
};

const submitStep = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  buttonDisabled.value = true;
  const isValid = await handleFormValidationErrors(previewRef.value.formErrors);
  if (!isValid) {
    errorMsg.value = "There are some errors with the document data";
    showLoader.value = false;
    buttonDisabled.value = false;
    return;
  }
  try {
    await order.value.saveUpdates();
    await intakeService.postOrdersSubmitClinicalReview(order.value.id);
    // Redirect to kanban board, depending on submission method (i.e. CoverMyMeds) some orders will
    // require standby time before status will switch from Created to Requires Review
    router.push({ name: "all-patients" });
  } catch (ex) {
    errorMsg.value = `Error submitting the review: ${ex.message}`;
  } finally {
    showLoader.value = false;
    buttonDisabled.value = false;
  }
};

const fetchIntakeOrder = async () => {
  showLoader.value = true;
  errorMsg.value = "";
  try {
    const orderData = await intakeService.getIntakeOrder(orderId);
    const orderObj = new IntakeOrder(orderData, dateAdapter);
    intakeStore.intakeOrder = orderObj;
    intakeStore.insuranceCards = orderObj.insuranceCard ? [orderObj.insuranceCard] : [];
  } catch (ex) {
    errorMsg.value = `Error fetching draft order: ${ex.message ?? ex}`;
  } finally {
    showLoader.value = false;
  }
};

const fetchPages = async () => {
  try {
    const pageResults = await intakeService.getPatientDocumentPages(order.value.patient.id);
    const pages = [order.value.orderForm.pages[0], order.value.insuranceCard.pages[0]];
    // All pages except the order form and insurance card
    pageResults.results
      .filter((p) => p.id !== pages[0].id && p.id !== pages[1].id)
      .forEach((p) => pages.push(new IntakeDocumentPage(p, dateAdapter)));
    intakeStore.setPageManager(pages, currentPage);
  } catch (ex) {
    errorMsg.value = `Error fetching pages: ${ex.message ?? ex}`;
  } finally {
    showLoader.value = false;
  }
};

const onAttributeEdit = (field, value, type) => {
  if (type === DOCUMENT_CLASSIFICATIONS.ORDER_FORM) {
    order.value?.orderForm.updateAttribute(field.key, value);
    order.value?.orderForm.debouncedSaveUpdates();
  } else if (type === DOCUMENT_CLASSIFICATIONS.INSURANCE_CARD) {
    order.value?.insuranceCard.updateAttribute(field.key, value);
    order.value?.insuranceCard.debouncedSaveUpdates();
  }
};

const onSnooze = async ({ minutes }) => {
  try {
    errorMsg.value = "";
    showLoader.value = true;
    const response = await intakeService.postSnoozeOrder(orderId, minutes);
    intakeStore.intakeOrder = new IntakeOrder(response, dateAdapter);
    if (minutes > 0) {
      router.push({ name: "all-patients" });
    }
  } catch (e) {
    errorMsg.value = `Error snoozing order: ${e.message}`;
  } finally {
    showLoader.value = false;
  }
};

const onArchive = async () => {
  if (order.value.status === INTAKE_STAGE.ARCHIVED) {
    try {
      await intakeService.deleteArchiveOrder(order.value.id);
      intakeStore.setReadOnly(false);
      return;
    } catch (e) {
      errorMsg.value = `Error unarchiving order: ${e.message}`;
    }
  }
  showArchiveReasonDialog.value = true;
};

const onArchived = () => {
  showArchiveReasonDialog.value = false;
  router.push({ name: "all-patients" });
};
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_patient-intake-review.scss";
</style>
